import { queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import { omit } from 'lodash-es';
import { z } from 'zod';
import { generateResponseScheme } from '@/models/response';
import { type FriendInfoResponseType, friendInfoResponseScheme } from '@/models/user/friend-info';
import { otherUserInfoScheme } from '@/models/user/other-user-info';
import { useUserId } from '@/stores/auth';
import { fetcher } from '@/utils/fetcher';

async function getFriendInfo(userId: Nullable<number>, signal: AbortSignal) {
  const otherUserInfo = await fetcher<OtherUserInfoResponse>(
    '/GetOtherUserInfo',
    { UserID: userId },
    { signal, schema: otherUserInfoResponseSchema, validResponseCodes: [0, 1] },
  ).then((response) =>
    response?.Data
      ? {
          ...response.Data,
          IsFriend: Boolean(response.IsFriend),
          IsBlackByFriend: Boolean(response.IsBlackByFriend),
        }
      : null,
  );

  if (!otherUserInfo) return null;

  if (!otherUserInfo.IsFriend)
    return {
      IsBlacklisted: otherUserInfo.IsBlackByFriend,
      IsFriend: otherUserInfo.IsFriend,
      FriendInfo: {
        FriendID: otherUserInfo.UID,
        FriendNickName: otherUserInfo.NickName,
        FriendHeadImg: otherUserInfo.HeadImg,
        FriendGender: otherUserInfo.Gender,
        FriendSign: otherUserInfo.Sign,
        Type: 0,
        IsOnline: false,
        OnlineDesc: '',
        RemarkName: '',
      },
    };

  const friendInfo = await fetcher<FriendInfoResponseType>(
    '/GetFriendInfo',
    { UserID: userId },
    { signal, schema: friendInfoResponseScheme, validResponseCodes: [0, 1] },
  ).then((response) => omit(response, ['Code', 'Msg']));

  return {
    IsBlacklisted: otherUserInfo.IsBlackByFriend,
    IsFriend: otherUserInfo.IsFriend,
    FriendInfo: {
      ...friendInfo?.FriendInfo,
      IsOnline: Boolean(friendInfo.FriendInfo.IsOnline),
      FriendID: friendInfo?.FriendInfo?.UID,
      FriendNickName: friendInfo?.FriendInfo?.NickName,
      FriendHeadImg: friendInfo?.FriendInfo?.HeadImg,
      FriendSign: friendInfo?.FriendInfo?.Sign,
      VChatID: friendInfo?.FriendInfo?.VChatID,
    },
    CommonGroup: friendInfo?.CommonGroup,
  };
}

export const generateFriendInfoQueryOptions = (userId: Nullable<number>) =>
  queryOptions({
    queryKey: ['/GetFriendInfo', userId],
    queryFn: userId ? ({ signal }) => getFriendInfo(userId, signal) : skipToken,
  });

export default function useFriendInfo(userId?: Nullable<number>) {
  const currentUserId = useUserId();
  const query = useQuery(generateFriendInfoQueryOptions(userId));

  return {
    friendInfo: query.data,
    ...query,
    isMe: currentUserId === userId,
    isFriend: Boolean(query.data?.IsFriend),
    isBlacklisted: Boolean(query.data?.IsBlacklisted),
  };
}

const otherUserInfoResponseSchema = generateResponseScheme(otherUserInfoScheme).extend({
  IsFriend: z.number().pipe(z.coerce.boolean()).nullish().default(0),
  IsBlackByFriend: z.number().pipe(z.coerce.boolean()).nullish().default(0),
});

type OtherUserInfoResponse = z.infer<typeof otherUserInfoResponseSchema>;
